import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import { obterEmpresaPorId } from '../../data/empresas';
import { getStatusLoja, formatarHorarioFuncionamento } from '../../utils/storeHours';

const StoreDetail = () => {
  const [searchParams] = useSearchParams();
  const storeId = searchParams?.get('id') || '1';
  const [activeTab] = useState('overview');

  // Obter dados da empresa
  const empresa = obterEmpresaPorId(storeId);
  
  // Se não encontrar a empresa, usar a primeira como fallback
  const storeData = empresa ? {
    id: empresa.id,
    name: empresa.nome,
    category: empresa.tipo,
    subcategory: empresa.tipo,
    description: empresa.descricao,
    owner: "Proprietário",
    ownerStory: `História inspiradora do proprietário do ${empresa.nome}.`,
    floor: empresa.andar,
    phone: empresa.telefone,
    email: empresa.email,
    website: empresa.site,
    instagram: empresa.redesSociais.instagram,
    facebook: empresa.redesSociais.facebook,
    whatsapp: empresa.whatsapp,
    logo: empresa.logo,
    logoSecundario: empresa.logoSecundario,
    rating: empresa.avaliacao,
    reviewCount: empresa.numeroAvaliacoes,
    status: getStatusLoja(empresa.horarioFuncionamento),
    horarioFuncionamento: empresa.horarioFuncionamento,
    hours: {
      monday: empresa.horarioFuncionamento,
      tuesday: empresa.horarioFuncionamento,
      wednesday: empresa.horarioFuncionamento,
      thursday: empresa.horarioFuncionamento,
      friday: empresa.horarioFuncionamento,
      saturday: empresa.horarioFuncionamento,
      sunday: empresa.horarioFuncionamento
    },
    amenities: empresa.comodidades,
    specialties: empresa.especialidades,
    priceRange: empresa.preco,
    images: [
      {
        url: empresa.imagem,
        caption: `Ambiente do ${empresa.nome}`
      },
      {
        url: empresa.imagem,
        caption: `Produtos do ${empresa.nome}`
      },
      {
        url: empresa.imagem,
        caption: `Serviços do ${empresa.nome}`
      },
      {
        url: empresa.imagem,
        caption: `Espaço do ${empresa.nome}`
      }
    ],
    services: empresa.servicos.map((servico, index) => ({
      id: index + 1,
      name: servico,
      duration: "1 hora",
      price: "Consulte",
      description: `Serviço de ${servico.toLowerCase()}`
    }))
  } : {
    id: "1",
    name: "Empresa não encontrada",
    category: "Indefinido",
    subcategory: "Indefinido",
    description: "Empresa não encontrada",
    owner: "N/A",
    ownerStory: "N/A",
    floor: "N/A",
    phone: "(11) 99999-0000",
    email: "contato@empresa.com.br",
    website: "www.empresa.com.br",
    instagram: "@empresa",
    facebook: "https://facebook.com/empresa",
    whatsapp: "(11) 99999-0000",
    logo: null,
    rating: 0,
    reviewCount: 0,
    status: "closed",
    hours: {
      monday: "Fechado",
      tuesday: "Fechado",
      wednesday: "Fechado",
      thursday: "Fechado",
      friday: "Fechado",
      saturday: "Fechado",
      sunday: "Fechado"
    },
    amenities: [],
    specialties: [],
    priceRange: "N/A",
    images: [],
    services: []
  };


  const tabs = [
    { id: 'overview', name: 'Informações', icon: 'Home' }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={index < Math.floor(rating) ? 'text-accent fill-current' : 'text-muted-foreground'}
      />
    ));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'text-success';
      case 'closed':
        return 'text-destructive';
      case 'closing_soon':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'open':
        return 'Aberto agora';
      case 'closed':
        return 'Fechado';
      case 'closing_soon':
        return 'Fechando em breve';
      default:
        return 'Status desconhecido';
    }
  };

  const formatPhoneNumber = (value) => {
    const digits = value?.toString().replace(/\D/g, '');
    if (!digits) return value || '';

    let countryPrefix = '';
    let localDigits = digits;

    if (digits.length === 13 && digits.startsWith('55')) {
      countryPrefix = '+55 ';
      localDigits = digits.slice(2);
    }

    if (localDigits.length === 11) {
      return `${countryPrefix}(${localDigits.slice(0, 2)}) ${localDigits.slice(2, 7)}-${localDigits.slice(7)}`;
    }

    if (localDigits.length === 10) {
      return `${countryPrefix}(${localDigits.slice(0, 2)}) ${localDigits.slice(2, 6)}-${localDigits.slice(6)}`;
    }

    return `${countryPrefix}${digits}`;
  };



  const handleCall = () => {
    window.open(`tel:${storeData?.phone}`, '_self');
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(`Olá! Gostaria de saber mais sobre o ${storeData?.name}.`);
    window.open(`https://wa.me/55${storeData?.whatsapp?.replace(/\D/g, '')}?text=${message}`, '_blank');
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: storeData?.name,
          text: storeData?.description,
          url: window.location?.href
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      navigator.clipboard?.writeText(window.location?.href);
      alert('Link copiado para a área de transferência!');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-muted/30 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex items-center space-x-2 text-sm">
              <Link to="/homepage" className="text-muted-foreground hover:text-foreground transition-colors">
                Início
              </Link>
              <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
              <Link to="/store-directory" className="text-muted-foreground hover:text-foreground transition-colors">
                Lojas
              </Link>
              <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
              <span className="text-foreground font-medium">{storeData?.name}</span>
            </nav>
          </div>
        </div>

        {/* Store Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between space-y-6 lg:space-y-0">
              <div className="flex-1">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Image
                      src={storeData?.images?.[0]?.url || storeData?.logo}
                      alt={`Foto ${storeData?.name}`}
                      className="w-32 h-32 rounded-xl object-cover border-2 border-border shadow-lg"
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <h1 className="text-2xl font-heading font-bold text-foreground">
                        {storeData?.name}
                      </h1>
                      <div className={`px-2 py-1 rounded-lg text-xs font-medium ${
                        storeData?.status === 'open' ? 'bg-success/20 text-success' :
                        storeData?.status === 'closed'? 'bg-destructive/20 text-destructive' : 'bg-warning/20 text-warning'
                      }`}>
                        {getStatusText(storeData?.status)}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4 mb-3">
                      <div className="flex items-center space-x-1">
                        {renderStars(storeData?.rating)}
                        <span className="text-sm font-medium text-foreground ml-1">
                          {storeData?.rating}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          ({storeData?.reviewCount} avaliações)
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center space-x-1">
                        <Icon name="MapPin" size={14} />
                        <span>{storeData?.floor}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Tag" size={14} />
                        <span>{storeData?.category}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                
                <Button
                  variant="outline"
                  iconName="Share2"
                  iconPosition="left"
                  onClick={handleShare}
                >
                  Compartilhar
                </Button>
                
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-background border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                size="sm"
                iconName="Whatsapp"
                iconPosition="left"
                onClick={handleWhatsApp}
              >
                WhatsApp
              </Button>
              {storeData?.instagram && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Instagram"
                  iconPosition="left"
                  onClick={() => window.open(`https://instagram.com/${storeData?.instagram?.replace('@', '')}`, '_blank')}
                >
                  Instagram
                </Button>
              )}
            </div>
          </div>
        </div>


        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Descrição Breve */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  Sobre o {storeData?.name}
                </h2>
                <div className="space-y-4">
                  <p className="text-sm text-foreground leading-relaxed">
                    {storeData?.description}
                  </p>
                  {!!storeData?.specialties?.length && (
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">
                        Especialidades
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {storeData.specialties.map((specialty, index) => (
                          <span
                            key={`${specialty}-${index}`}
                            className="inline-flex items-center rounded-full border border-border bg-muted/40 px-3 py-1 text-xs text-foreground"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Informações Principais */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Horário de Funcionamento */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center">
                    <Icon name="Clock" size={20} className="mr-2 text-primary" />
                    Horário de Funcionamento
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-4 text-sm">
                      <span className="text-muted-foreground">Status</span>
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                          storeData?.status === 'open'
                            ? 'bg-success/15 text-success'
                            : storeData?.status === 'closed'
                            ? 'bg-destructive/15 text-destructive'
                            : 'bg-warning/15 text-warning'
                        }`}
                      >
                        {getStatusText(storeData?.status)}
                      </span>
                    </div>
                    <div className="h-px bg-border/60" />
                    <div className="space-y-2 text-sm">
                      <span className="text-muted-foreground">Horário</span>
                      <p className="text-foreground font-medium leading-relaxed break-words">
                        {formatarHorarioFuncionamento(empresa?.horarioFuncionamento || storeData?.horarioFuncionamento) || 'Horário não disponível'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Nicho */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center">
                    <Icon name="Tag" size={20} className="mr-2 text-primary" />
                    Nicho
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-4 text-sm">
                      <span className="text-muted-foreground">Categoria</span>
                      <span className="text-foreground font-medium text-right">
                        {storeData?.category}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4 text-sm">
                      <span className="text-muted-foreground">Andar</span>
                      <span className="text-foreground font-medium text-right">
                        {storeData?.floor}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Redes Sociais */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                  Redes Sociais
                </h3>
                <div className="flex flex-wrap gap-3">
                  {storeData?.instagram && (
                    <Button
                      variant="outline"
                      iconName="Instagram"
                      iconPosition="left"
                      onClick={() => {
                        const instagramUrl = storeData?.instagram?.startsWith('http') 
                          ? storeData.instagram 
                          : `https://instagram.com/${storeData.instagram.replace('@', '')}`;
                        window.open(instagramUrl, '_blank');
                      }}
                    >
                      Instagram
                    </Button>
                  )}
                  
                  {storeData?.facebook && (
                    <Button
                      variant="outline"
                      iconName="Facebook"
                      iconPosition="left"
                      onClick={() => {
                        const facebookUrl = storeData?.facebook?.startsWith('http')
                          ? storeData.facebook
                          : `https://facebook.com/${storeData.facebook}`;
                        window.open(facebookUrl, '_blank');
                      }}
                    >
                      Facebook
                    </Button>
                  )}
                  
                  {!storeData?.instagram && !storeData?.facebook && (
                    <p className="text-muted-foreground text-sm">
                      Redes sociais não disponíveis
                    </p>
                  )}
                </div>
              </div>

              {/* Contato WhatsApp */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center">
                  <Icon name="Whatsapp" size={20} className="mr-2 text-primary" />
                  Contato pelo WhatsApp
                </h3>
                <div className="flex items-center gap-4">
                  {storeData?.whatsapp ? (
                    <>
                      <Button
                        variant="default"
                        iconName="Whatsapp"
                        iconPosition="left"
                        onClick={handleWhatsApp}
                      >
                        Entrar em Contato
                      </Button>
                    </>
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      WhatsApp não disponível
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

      </main>
    </div>
  );
};

export default StoreDetail;